/**
 * Jun-2026 (User-Wunsch) — kompakte Boni-Status-Übersicht in der Disposition,
 * angeordnet unter dem KI-Dokumentenarchiv. Read-only: zeigt die verbuchten
 * Boni/Aktions-Boni des Fahrzeugs mit Status (ausstehend/eingegangen),
 * Eingangsdatum & Belegnummer. Erfassung erfolgt im „Zahlungseingänge/Boni"-Modal.
 */
import { useEffect, useState } from "react";
import { Gift, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import api, { formatPriceExact as formatPrice } from "../../lib/api";

// Überfälligkeits-Stufe aus Erfassungsdatum (60/90/120 Tage) — analog Backend.
const OVERDUE_THRESHOLDS = [60, 90, 120];
function overdueLevel(recordedAt, status) {
    if (status === "eingegangen" || !recordedAt) return { level: 0, days: null };
    const d = new Date(recordedAt);
    if (isNaN(d.getTime())) return { level: 0, days: null };
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    let level = 0;
    OVERDUE_THRESHOLDS.forEach((th, i) => { if (days >= th) level = i + 1; });
    return { level, days };
}

export default function BoniStatusCard({ listingId, refreshKey }) {
    const [boni, setBoni] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!listingId) return;
        let on = true;
        api.get(`/listings/${listingId}/dispo/payment-receipts`)
            .then((r) => {
                if (!on) return;
                setBoni((r.data?.entries || []).filter((e) => e.type === "bonus"));
            })
            .catch(() => { if (on) setBoni([]); })
            .finally(() => { if (on) setLoaded(true); });
        return () => { on = false; };
    }, [listingId, refreshKey]);

    if (!listingId || !loaded || boni.length === 0) return null;
    const open = boni.filter((b) => (b.status || "ausstehend") !== "eingegangen").reduce((s, b) => s + (Number(b.amount) || 0), 0);
    const received = boni.filter((b) => b.status === "eingegangen").reduce((s, b) => s + (Number(b.amount) || 0), 0);

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4 mt-4" data-testid="dispo-boni-status-card">
            <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-amber-500" />
                    <h4 className="font-display font-bold text-sm">Boni-Nachverfolgung</h4>
                </div>
                <div className="text-[11px] font-mono-num text-right">
                    <span className="text-amber-700 font-bold">{formatPrice(open)} offen</span>
                    <span className="text-gray-300 mx-1">|</span>
                    <span className="text-emerald-700 font-bold">{formatPrice(received)} erhalten</span>
                </div>
            </div>
            <ul className="divide-y divide-gray-100 border border-gray-100 rounded-sm" data-testid="dispo-boni-status-list">
                {boni.map((b) => {
                    const eingegangen = b.status === "eingegangen";
                    const od = overdueLevel(b.recorded_at, b.status);
                    return (
                        <li key={b.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs" data-testid={`dispo-boni-row-${b.id}`}>
                            <div className="min-w-0">
                                <span className="font-medium truncate">{b.bonus_type || "Boni"}</span>
                                {(eingegangen && (b.date || b.belegnummer)) && (
                                    <span className="text-[10px] text-gray-400 ml-1 font-mono-num">{b.date || ""}{b.belegnummer ? ` · ${b.belegnummer}` : ""}</span>
                                )}
                                {od.level > 0 && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 rounded-sm px-1 py-0.5 ml-1 align-middle" data-testid={`dispo-boni-overdue-${b.id}`}>
                                        <AlertTriangle className="w-3 h-3" /> überfällig ({od.days} T.)
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <span className="font-mono-num font-bold">{formatPrice(b.amount)}</span>
                                {eingegangen ? (
                                    <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /></span>
                                ) : (
                                    <span className={`inline-flex items-center gap-1 ${od.level > 0 ? "text-red-600" : "text-amber-600"}`}><Clock className="w-3.5 h-3.5" /></span>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
